import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GcButtonComponent,
  GcTableRow,
  GcTextComponent,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { map } from 'rxjs';
import { paginateArray } from '../../../shared/pagination.helper';
import { SharedModule } from '../../../shared/shared.module';
import { ResourcesService } from '../../../user/resources.service';
import {
  Building,
  DeliveryPoint,
  InstallationReportingMduReportContext,
} from '../../entities/installation-reporting-mdu-details.entities';
import {
  Buildings,
  DeliveryPoints,
  GroupAgreementsDetails,
} from '../../entities/installation-reporting-mdu.entities';
import {
  detailsToInfoCards,
  InfoCard,
} from '../../helpers/group-agreement-details.helper';
import {
  filterTableRowsBySearch,
  mapBuildingTableRows,
  mapDeliveryPointTableRows,
} from '../../helpers/report-details-table.helper';
import {
  buildingColumns,
  deliveryPointColumns,
} from '../group-agreement-details-table/group-agreement-details-table.columns';
import { GroupAgreementDetailsTableComponent } from '../group-agreement-details-table/group-agreement-details-table.component';
import { InstallationReportingMduApiService } from '../installation-reporting-mdu-api.service';
import { InstallationReportingMduModalComponent } from '../installation-reporting-mdu-modal/installation-reporting-mdu-modal.component';
import {
  mapBuildingInstallationReportToBuilding,
  mapDeliveryPointInstallationReportToDeliveryPoint,
} from '../installation-reporting-mdu.mapper';

export type tableType = 'BUILDINGS' | 'DELIVERY_POINTS';

function calculateNumberOfPages(
  totalItems: number,
  pageSize: number | null,
): number {
  return pageSize ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;
}

@Component({
  selector: 'app-group-agreement-details-wrapper',
  imports: [
    GcTextComponent,
    GcButtonComponent,
    IpoTranslatePipe,
    SharedModule,
    GroupAgreementDetailsTableComponent,
    InstallationReportingMduModalComponent,
  ],
  templateUrl: './group-agreement-details-wrapper.component.html',
  styleUrl: './group-agreement-details-wrapper.component.scss',
})
export class GroupAgreementDetailsWrapperComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InstallationReportingMduApiService);
  private readonly resourcesService = inject(ResourcesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly ifsNumber = toSignal(
    this.resourcesService
      .getSelectedResource$()
      .pipe(map((resource) => resource?.value ?? null)),
    { initialValue: null },
  );

  readonly groupAgreementId = toSignal(
    this.route.queryParamMap.pipe(map((params) => params.get('id'))),
    { initialValue: null },
  );

  groupAgreementDetails = signal<GroupAgreementsDetails | null>(null);
  buildings = signal<Buildings>({
    items: [],
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    currentCount: 0,
  });
  deliveryPoints = signal<DeliveryPoints>({
    items: [],
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    currentCount: 0,
  });

  mappedBuildings = signal<Building[]>([]);
  mappedDeliveryPoints = signal<DeliveryPoint[]>([]);
  selectedReport = signal<InstallationReportingMduReportContext | null>(null);

  currentItemsPerPageBuildings = signal<number | null>(5);
  activePageBuildings = signal<number>(0);
  buildingSearchQuery = signal<string>('');

  currentItemsPerPageDeliveryPoints = signal<number | null>(5);
  activePageDeliveryPoints = signal<number>(0);
  deliveryPointSearchQuery = signal<string>('');

  allBuildingRows = computed<GcTableRow[]>(() =>
    mapBuildingTableRows(this.buildings().items),
  );
  allDeliveryPointRows = computed<GcTableRow[]>(() =>
    mapDeliveryPointTableRows(this.deliveryPoints().items),
  );

  filteredBuildingRows = computed<GcTableRow[]>(() =>
    filterTableRowsBySearch(this.allBuildingRows(), this.buildingSearchQuery()),
  );
  filteredDeliveryPointRows = computed<GcTableRow[]>(() =>
    filterTableRowsBySearch(
      this.allDeliveryPointRows(),
      this.deliveryPointSearchQuery(),
    ),
  );

  numberOfPagesBuildings = computed<number>(() =>
    calculateNumberOfPages(
      this.filteredBuildingRows().length,
      this.currentItemsPerPageBuildings(),
    ),
  );
  numberOfPagesDeliveryPoints = computed<number>(() =>
    calculateNumberOfPages(
      this.filteredDeliveryPointRows().length,
      this.currentItemsPerPageDeliveryPoints(),
    ),
  );

  tableRowsBuildings = computed<GcTableRow[]>(() => {
    const pageSize = this.currentItemsPerPageBuildings();
    const rows = this.filteredBuildingRows();
    return pageSize
      ? paginateArray(rows, pageSize, this.activePageBuildings())
      : rows;
  });
  tableRowsDeliveryPoints = computed<GcTableRow[]>(() => {
    const pageSize = this.currentItemsPerPageDeliveryPoints();
    const rows = this.filteredDeliveryPointRows();
    return pageSize
      ? paginateArray(rows, pageSize, this.activePageDeliveryPoints())
      : rows;
  });

  buildingTableColumn = buildingColumns;
  pointTableColumn = deliveryPointColumns;

  infoCards = computed<InfoCard[]>(() =>
    detailsToInfoCards(this.groupAgreementDetails()),
  );

  private readonly openedFromIfs = (history.state as { ifs?: string } | null)
    ?.ifs;

  constructor() {
    effect(() => {
      const ifsNumber = this.ifsNumber();
      const groupAgreementId = this.groupAgreementId();
      if (!this.checkIfsNumber(ifsNumber)) {
        return;
      }
      this.loadData(ifsNumber, groupAgreementId);
    });
  }

  /**
   * Returns true when the selected IFS matches the IFS the page was opened from.
   * Otherwise navigates back to the installation reporting overview and returns false.
   */
  checkIfsNumber(ifsNumber: string | null): boolean {
    if (ifsNumber !== this.openedFromIfs) {
      this.navigateToInstallationReportingPage();
      return false;
    }
    return true;
  }

  loadData(ifsNumber: string | null, groupAgreementId: string | null): void {
    if (ifsNumber && groupAgreementId) {
      this.loadGroupAgreementDetails(groupAgreementId);
      this.loadBuildings(groupAgreementId);
      this.loadDeliveryPoints(ifsNumber, groupAgreementId);
    }
  }

  onBuildingRowClicked(row: GcTableRow): void {
    const buildingId = row['buildingId'] as string | undefined;
    if (!buildingId) {
      return;
    }
    const building = this.mappedBuildings().find(
      (item) => item.id === buildingId,
    );
    if (!building) {
      return;
    }
    this.selectedReport.set({
      reportType: 'BUILDING',
      building,
      path: 'buildings',
    });
  }

  onDeliveryPointRowClicked(row: GcTableRow): void {
    const deliveryPointId = (row['deliveryPointId'] ?? row['buildingId']) as
      | string
      | undefined;
    if (!deliveryPointId) {
      return;
    }
    const deliveryPoint = this.mappedDeliveryPoints().find(
      (item) => item.id === deliveryPointId,
    );
    if (!deliveryPoint) {
      return;
    }
    this.selectedReport.set({
      reportType: 'DELIVERY_POINT',
      deliveryPoint,
      path: 'delivery-points',
    });
  }

  closeReportModal(): void {
    this.selectedReport.set(null);
  }

  onReportSaved(): void {
    const selectedReport = this.selectedReport();
    const ifsNumber = this.ifsNumber();
    const groupAgreementId = this.groupAgreementId();
    if (
      selectedReport?.reportType === 'DELIVERY_POINT' &&
      ifsNumber &&
      groupAgreementId
    ) {
      this.loadDeliveryPoints(ifsNumber, groupAgreementId);
    }
    if (selectedReport?.reportType === 'BUILDING' && groupAgreementId) {
      this.loadBuildings(groupAgreementId);
    }
  }

  navigateToInstallationReportingPage(): void {
    const ifs = this.ifsNumber();
    if (ifs) {
      this.router.navigate(['/installation-reporting', ifs]);
      return;
    }
    this.router.navigate(['/installation-reporting']);
  }

  onBuildingsPageChange(newPage: number): void {
    this.activePageBuildings.set(newPage);
  }

  onBuildingsPageSizeChange(newPageSize: number | null): void {
    this.currentItemsPerPageBuildings.set(newPageSize);
    this.resetActivePage('BUILDINGS');
  }

  onDeliveryPointsPageChange(newPage: number): void {
    this.activePageDeliveryPoints.set(newPage);
  }

  onDeliveryPointsPageSizeChange(newPageSize: number | null): void {
    this.currentItemsPerPageDeliveryPoints.set(newPageSize);
    this.resetActivePage('DELIVERY_POINTS');
  }

  onBuildingSearchChanged(searchQuery: string): void {
    this.buildingSearchQuery.set(searchQuery);
    this.resetActivePage('BUILDINGS');
  }

  onDeliveryPointSearchChanged(searchQuery: string): void {
    this.deliveryPointSearchQuery.set(searchQuery);
    this.resetActivePage('DELIVERY_POINTS');
  }

  resetActivePage(type: tableType) {
    if (type === 'BUILDINGS') {
      this.activePageBuildings.set(0);
    } else {
      this.activePageDeliveryPoints.set(0);
    }
  }

  private loadGroupAgreementDetails(groupAgreementId: string) {
    this.api
      .getGroupAgreementDetails(groupAgreementId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((details) => this.groupAgreementDetails.set(details));
  }

  private loadBuildings(groupAgreementId: string) {
    this.api
      .getBuildings(groupAgreementId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        const items = response.items ?? [];
        this.buildings.set({
          items,
          totalItems: response.totalItems ?? items.length,
          totalPages: response.totalPages ?? 1,
          currentPage: response.currentPage ?? 1,
          currentCount: response.currentCount ?? items.length,
        });
        this.mappedBuildings.set(
          items.map(mapBuildingInstallationReportToBuilding),
        );
      });
  }

  private loadDeliveryPoints(ifsNumber: string, groupAgreementId: string) {
    this.api
      .getDeliveryPoints(ifsNumber, groupAgreementId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        const items = response.items ?? [];
        this.deliveryPoints.set({
          items,
          totalItems: response.totalItems ?? items.length,
          totalPages: response.totalPages ?? 1,
          currentPage: response.currentPage ?? 1,
          currentCount: response.currentCount ?? items.length,
        });
        this.mappedDeliveryPoints.set(
          items.map(mapDeliveryPointInstallationReportToDeliveryPoint),
        );
      });
  }
}
